/**
 * Unidades y conversiones.
 *
 * Unidad base por dimension:  masa -> g   volumen -> mL   recuento -> pz
 * Los costes internos se calculan SIEMPRE por unidad base: es lo que permite
 * mezclar en una receta gramos, litros y piezas sin errores de redondeo.
 *
 * Ojo con la distincion: la unidad base es de calculo, NO de presentacion.
 * Quien compra un saco de 25 kg quiere leer "0,71 €/kg", no "0,00071 €/g"
 * (lo pidio Alex el 12 de agosto). Para eso esta `costPerPurchaseUnit`.
 */

export type Dimension = 'mass' | 'volume' | 'count';

export type Unit =
  | 'g'
  | 'kg'
  | 'lb'
  | 'oz'
  | 'ml'
  | 'cl'
  | 'l'
  | 'taza'
  | 'cucharada'
  | 'cucharadita'
  | 'pz'
  | 'docena';

interface UnitDef {
  dimension: Dimension;
  /** cuantas unidades base equivale 1 de esta unidad */
  factor: number;
  label: string;
  /** true si es un formato valido de compra (aparece en el alta de ingrediente) */
  purchase?: boolean;
}

export const UNITS: Record<Unit, UnitDef> = {
  g: { dimension: 'mass', factor: 1, label: 'g', purchase: true },
  kg: { dimension: 'mass', factor: 1000, label: 'kg', purchase: true },
  lb: { dimension: 'mass', factor: 453.59, label: 'lb', purchase: true },
  oz: { dimension: 'mass', factor: 28.35, label: 'oz', purchase: true },
  ml: { dimension: 'volume', factor: 1, label: 'mL', purchase: true },
  cl: { dimension: 'volume', factor: 10, label: 'cL', purchase: true },
  l: { dimension: 'volume', factor: 1000, label: 'L', purchase: true },
  taza: { dimension: 'volume', factor: 240, label: 'taza' },
  cucharada: { dimension: 'volume', factor: 15, label: 'cucharada' },
  cucharadita: { dimension: 'volume', factor: 5, label: 'cucharadita' },
  pz: { dimension: 'count', factor: 1, label: 'pz', purchase: true },
  docena: { dimension: 'count', factor: 12, label: 'docena', purchase: true },
};

export const BASE_UNIT: Record<Dimension, Unit> = {
  mass: 'g',
  volume: 'ml',
  count: 'pz',
};

/**
 * Formatos en los que se puede dar de alta un ingrediente. Es un subconjunto
 * de Unit: nadie compra "una cucharada" de nada, y el tipo lo dice para que
 * no se cuele por descuido.
 */
export type PurchaseUnit = 'g' | 'kg' | 'lb' | 'oz' | 'ml' | 'cl' | 'l' | 'pz' | 'docena';

export const PURCHASE_UNITS: PurchaseUnit[] = (Object.keys(UNITS) as Unit[]).filter(
  (u) => UNITS[u].purchase,
) as PurchaseUnit[];

export function isPurchaseUnit(unit: Unit | undefined): unit is PurchaseUnit {
  return unit !== undefined && Boolean(UNITS[unit]?.purchase);
}

/**
 * Sistema de medidas del restaurante. NO cambia como se calcula nada: solo
 * decide que unidades se ofrecen primero y cual se propone por defecto.
 * Un restaurante que compra en libras no deberia tener que bucear en una
 * lista encabezada por kilos, pero tampoco queremos convertirle los datos
 * que ya tiene guardados.
 */
export type UnitSystem = 'metrico' | 'imperial';

const ORDEN_UNIDADES: Record<UnitSystem, PurchaseUnit[]> = {
  metrico: ['kg', 'g', 'l', 'ml', 'cl', 'pz', 'docena', 'lb', 'oz'],
  imperial: ['lb', 'oz', 'pz', 'docena', 'l', 'ml', 'cl', 'kg', 'g'],
};

/** Formatos de compra ordenados segun el sistema del restaurante. */
export function purchaseUnitsFor(system: UnitSystem = 'metrico'): PurchaseUnit[] {
  const orden = ORDEN_UNIDADES[system] ?? ORDEN_UNIDADES.metrico;
  const disponibles = new Set(PURCHASE_UNITS);
  return orden.filter((u) => disponibles.has(u));
}

/** Unidad que se propone por defecto al crear un ingrediente. */
export function defaultPurchaseUnit(system: UnitSystem = 'metrico'): PurchaseUnit {
  return system === 'imperial' ? 'lb' : 'kg';
}

/** Presentaciones comerciales del catalogo de Alex. */
export const PRESENTATION_UNITS = [
  'ampolleta', 'barril', 'bidón', 'blíster', 'bolsa', 'bote', 'botella', 'caja',
  'cartón', 'cesta', 'cilindro', 'costal', 'cubeta', 'docena', 'dúo', 'embalaje',
  'envase', 'estuche', 'fardo', 'frasco', 'galón', 'garrafón', 'granel', 'jarra',
  'kg', 'kit', 'L', 'lata', 'malla', 'metro', 'paquete', 'par', 'pz', 'plancha',
  'rollo', 'saco', 'sobre', 'tarro',
] as const;

/**
 * Donde esta el dato que hay que corregir.
 *
 * El mensaje del error ya dice QUE pasa; esto dice DONDE se arregla, para que
 * la pantalla pueda llevar al usuario alli en vez de abrirle el plato, que es
 * justo donde no puede tocar nada (peticion de Alex, 1 sep 2026: "si Alexia ya
 * sabe que esta mal y donde, que te lleve al sitio").
 */
export interface CostingCulprit {
  kind: 'recipe' | 'ingredient';
  id: string;
  name: string;
}

export class UnitError extends Error {
  readonly culprit?: CostingCulprit;

  constructor(message: string, culprit?: CostingCulprit) {
    super(message);
    this.name = 'UnitError';
    this.culprit = culprit;
  }
}

export function dimensionOf(unit: Unit): Dimension {
  const def = UNITS[unit];
  if (!def) throw new UnitError(`Unidad desconocida: "${unit}"`);
  return def.dimension;
}

/** Convierte una cantidad a su unidad base (g / mL / pz). */
export function toBase(qty: number, unit: Unit): number {
  const def = UNITS[unit];
  if (!def) throw new UnitError(`Unidad desconocida: "${unit}"`);
  return qty * def.factor;
}

export function fromBase(qtyInBase: number, unit: Unit): number {
  const def = UNITS[unit];
  if (!def) throw new UnitError(`Unidad desconocida: "${unit}"`);
  return qtyInBase / def.factor;
}

/**
 * Convierte entre dos unidades. Falla si no comparten dimension: no queremos
 * que nadie escriba "200 mL de harina" y obtenga un coste silenciosamente mal.
 */
export function convert(qty: number, from: Unit, to: Unit): number {
  if (dimensionOf(from) !== dimensionOf(to)) {
    throw new UnitError(
      `No se puede convertir de ${UNITS[from].label} a ${UNITS[to].label}. ` +
        `Si el ingrediente se compra en peso y se usa en volumen, define su densidad.`,
    );
  }
  return toBase(qty, from) / UNITS[to].factor;
}

export function unitsForDimension(dimension: Dimension): Unit[] {
  return (Object.keys(UNITS) as Unit[]).filter((u) => UNITS[u].dimension === dimension);
}

/** Redondeo estable (evita 0.1 + 0.2 = 0.30000000000000004). */
export function round(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0;
  const f = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * f) / f;
}

/**
 * CONVENCION DE PORCENTAJES EN TODO EL PROYECTO
 * Todos los campos que acaban en `Pct` van de 0 a 100, nunca en tanto por uno.
 * IVA 10 % -> vatPct: 10.  Merma del 35 % -> wastePct: 35.
 * Es la forma en la que el duenyo del restaurante piensa y escribe los numeros.
 */
export const pctToRate = (pct: number): number => pct / 100;
export const rateToPct = (rate: number): number => rate * 100;
